/**
 * Service for searching gifs and managing search suggestions/history and explore endpoints.
 *
 * Responsibilities:
 * - Search gifs with filters (order/type/tags/verified/paging).
 * - Fetch tag suggestions for search input.
 * - Read and manage user search history (list/add/remove/clear).
 * - Fetch explore endpoints (top this week, trending gifs/images).
 */
import type { ApiClient } from "@/common/api/http/types";
import {
  IGifList,
  IAddToHistory,
  ITagsList,
  THistoryList,
  SearchGifsOrder,
} from "../types";

interface ISearchGifsRequest {
  query?: string;
  count: number;
  order: SearchGifsOrder;
  type?: string;
  page?: number;
  verified?: boolean;
  tags?: string[];
}

export default class SearchService {
  public constructor(private readonly client: ApiClient) {}

  async searchGifs(request: ISearchGifsRequest): Promise<IGifList> {
    const query = new URLSearchParams();

    request.type && query.append("type", request.type);
    request.order && query.append("order", request.order);
    request.count && query.append("count", request.count.toString());
    request.page && query.append("page", request.page.toString());
    request.query && query.append("query", request.query);
    request.verified && query.append("verified", "y");
    request.tags && query.append("tags", request.tags.join(","));

    return await this.client.get(`v2/gifs/search?${query.toString()}`);
  }

  async getTagsSearches(query: string): Promise<ITagsList[]> {
    return await this.client.get(`v2/search/suggest?query=${encodeURI(query)}`);
  }

  async getHistorySearches(): Promise<THistoryList> {
    return await this.client.get("v2/search/user-history");
  }

  async removeAllHistorySearches(): Promise<void> {
    return await this.client.delete("v2/search/user-history");
  }

  async removeHistorySearchItem(id: string): Promise<void> {
    return await this.client.delete(`v2/search/user-history/${id}`);
  }

  async addHistorySearchItem(data: IAddToHistory): Promise<void> {
    return await this.client.post("v2/search/user-history", data);
  }
}
